import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useProfiles } from '@/features/users/hooks';
import {
  useDeleteTicketCategory,
  useReorderTicketCategories,
  useSaveTicketCategory,
  useTicketCategories,
  type TicketCategory,
} from '@/features/tickets/hooks';
import { describeTicketError, ticketCategoryIcon } from '@/features/tickets/model';

const NOBODY = '__nobody__';

interface TicketCategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Admin: the kinds of request people can file and who each kind goes to
 * by default. A default handler turns "a manager will route it" into
 * "goes straight to Emily" for purchases, "goes to you" for access
 * requests, and so on. Inactive categories stay on old tickets but
 * disappear from the pickers.
 */
export function TicketCategoriesDialog({ open, onOpenChange }: TicketCategoriesDialogProps) {
  const categoriesQ = useTicketCategories({ includeInactive: true });
  const profilesQ = useProfiles();
  const save = useSaveTicketCategory();
  const reorder = useReorderTicketCategories();
  const remove = useDeleteTicketCategory();
  const [newName, setNewName] = useState('');

  const people = useMemo(
    () =>
      (profilesQ.data ?? [])
        .filter((p) => p.is_active && p.role !== 'external_lender')
        .sort((a, b) => (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email)),
    [profilesQ.data],
  );

  const categories = categoriesQ.data ?? [];

  const onError = (err: unknown) => toast.error(describeTicketError(err));

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    save.mutate(
      { name, position: categories.length + 1 },
      {
        onSuccess: () => setNewName(''),
        onError,
      },
    );
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= categories.length) return;
    const ids = categories.map((c) => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(ids, { onError });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ticket categories</DialogTitle>
          <DialogDescription>
            What people can ask for, and who hears about it first. Set a default handler and
            new tickets in that category route themselves; leave it empty and they wait in the
            inbox for a manager to route.
          </DialogDescription>
        </DialogHeader>

        {categoriesQ.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <ul className="space-y-2">
            {categories.map((c, index) => (
              <CategoryRow
                key={c.id}
                category={c}
                people={people}
                first={index === 0}
                last={index === categories.length - 1}
                busy={save.isPending || reorder.isPending || remove.isPending}
                onMoveUp={() => move(index, -1)}
                onMoveDown={() => move(index, 1)}
                onSave={(patch) =>
                  save.mutate({ id: c.id, name: c.name, ...patch }, { onError })
                }
                onDelete={() => {
                  if (
                    !window.confirm(
                      `Delete “${c.name}”? Existing tickets keep working but lose the category. Turning it off is usually enough.`,
                    )
                  )
                    return;
                  remove.mutate(c.id, { onError });
                }}
              />
            ))}
          </ul>
        )}

        <form onSubmit={add} className="flex items-end gap-2 border-t border-border pt-4">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="new-category">New category</Label>
            <Input
              id="new-category"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Packaging, Facilities, Training"
              maxLength={60}
            />
          </div>
          <Button type="submit" disabled={!newName.trim() || save.isPending} className="h-10">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CategoryRow({
  category,
  people,
  first,
  last,
  busy,
  onMoveUp,
  onMoveDown,
  onSave,
  onDelete,
}: {
  category: TicketCategory;
  people: Array<{ id: string; full_name: string | null; email: string }>;
  first: boolean;
  last: boolean;
  busy: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSave: (patch: {
    name?: string;
    description?: string | null;
    defaultAssigneeId?: string | null;
    isActive?: boolean;
  }) => void;
  onDelete: () => void;
}) {
  const Icon = ticketCategoryIcon(category.slug);
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description ?? '');
  useEffect(() => setName(category.name), [category.name]);
  useEffect(() => setDescription(category.description ?? ''), [category.description]);

  const defaultAssignee = category.default_assignee_id
    ? people.find((p) => p.id === category.default_assignee_id) ?? null
    : null;
  const defaultMissing = !!category.default_assignee_id && !defaultAssignee;

  return (
    <li
      className={cn(
        'rounded-lg border border-border bg-card p-3',
        !category.is_active && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary/60 text-muted-foreground">
          <Icon className="h-4 w-4" strokeWidth={1.7} />
        </div>
        <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                const next = name.trim();
                if (!next) {
                  setName(category.name);
                  return;
                }
                if (next !== category.name) onSave({ name: next });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              maxLength={60}
              aria-label={`Name for ${category.name}`}
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Goes to by default
            </Label>
            <Select
              value={category.default_assignee_id ?? NOBODY}
              onValueChange={(v) => onSave({ defaultAssigneeId: v === NOBODY ? null : v })}
            >
              <SelectTrigger className="h-9 w-full" aria-label={`Default handler for ${category.name}`}>
                <SelectValue>
                  {defaultAssignee
                    ? defaultAssignee.full_name ?? defaultAssignee.email
                    : defaultMissing
                      ? 'Former teammate — pick someone'
                      : 'Nobody — a manager routes it'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOBODY}>Nobody — a manager routes it</SelectItem>
                {people.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.full_name ?? p.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Hint shown when picked
            </Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => {
                const next = description.trim();
                if (next !== (category.description ?? '')) onSave({ description: next || null });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              placeholder="e.g. Include the supplier and part number if you know them."
              maxLength={200}
              aria-label={`Hint for ${category.name}`}
              className="h-9"
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={category.is_active}
            onCheckedChange={(checked) => onSave({ isActive: checked })}
            aria-label={`${category.name} active`}
          />
          {category.is_active ? 'Shown in pickers' : 'Hidden from pickers'}
        </label>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-9 w-9 p-0"
            onClick={onMoveUp}
            disabled={first || busy}
            aria-label={`Move ${category.name} up`}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-9 w-9 p-0"
            onClick={onMoveDown}
            disabled={last || busy}
            aria-label={`Move ${category.name} down`}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            disabled={busy}
            aria-label={`Delete ${category.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </li>
  );
}
